import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "../../components/DailyBudgetInvoiceItemsTable/aggridCustom.css";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { CellClickedEvent, ColDef, CellValueChangedEvent } from "ag-grid-community";
import { Alert, Box, Button, IconButton, Snackbar, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { useNavigate } from "react-router-dom";
import useUpdateAuthToken from "../../hooks/useUpdateAuthToken";
import {
  useGetBannersQuery,
  useDeleteBannerMutation,
  useUpdateBannerMutation,
} from "../../services/banners/banners";
import { BannerType } from "../../types/types";
import { PageLayout } from "../../components/PageLayout/PageLayout";
import { PageContentBox } from "../shared/styledComponents";
import { BANNER_LIST_TEXTS, SNACKBAR_AUTO_HIDE_MS } from "../shared/constants";
import { GREEN, RED } from "../../styles/mui-theme";
import DeleteBannerDialog from "./DeleteBannerDialog";

const TABLE_HEIGHT = "700px";

const BannerListPage: React.FC = () => {
  useUpdateAuthToken();
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact<BannerType> | null>(null);

  const { data: banners, isFetching } = useGetBannersQuery();
  const [deleteBanner, { isLoading: isDeleting }] = useDeleteBannerMutation();
  const [updateBanner] = useUpdateBannerMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState<BannerType | null>(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [editedRows, setEditedRows] = useState<Set<string>>(new Set());
  const [searchText, setSearchText] = useState("");

  const rowData = useMemo(
    () => (isFetching || !banners ? undefined : banners.map((b) => ({ ...b }))),
    [isFetching, banners],
  );

  const handleSaveRow = useCallback(
    async (banner: BannerType) => {
      const bannerId = banner.id;
      if (!bannerId) return;
      try {
        await updateBanner({
          id: bannerId,
          patch: {
            title: banner.title,
            link_url: banner.link_url,
            display_order: Number(banner.display_order),
            is_active: banner.is_active,
          },
        }).unwrap();
        setEditedRows((prev) => {
          const next = new Set(prev);
          next.delete(bannerId);
          return next;
        });
        setSuccessMsg(BANNER_LIST_TEXTS.saveSuccess);
      } catch {
        setErrorMsg(BANNER_LIST_TEXTS.saveError);
      }
    },
    [updateBanner],
  );

  const columnDefs: ColDef<BannerType>[] = useMemo(
    () => [
      {
        headerName: BANNER_LIST_TEXTS.colId,
        field: "id",
        filter: true,
        width: 200,
        cellStyle: { cursor: "pointer", color: GREEN, textDecoration: "underline" },
      },
      {
        headerName: BANNER_LIST_TEXTS.colTitle,
        field: "title",
        filter: true,
        flex: 1,
        editable: true,
      },
      {
        headerName: BANNER_LIST_TEXTS.colLinkUrl,
        field: "link_url",
        filter: true,
        flex: 1,
        editable: true,
      },
      {
        headerName: BANNER_LIST_TEXTS.colOrder,
        field: "display_order",
        width: 100,
        editable: true,
      },
      {
        headerName: BANNER_LIST_TEXTS.colActive,
        field: "is_active",
        width: 100,
        editable: true,
        cellEditor: "agCheckboxCellEditor",
        cellRenderer: "agCheckboxCellRenderer",
        cellStyle: (params) => ({
          color: params.value ? GREEN : RED,
          fontWeight: "bold",
        }),
      },
      {
        headerName: BANNER_LIST_TEXTS.colActions,
        width: 120,
        pinned: "right",
        sortable: false,
        filter: false,
        cellRenderer: (params: { data: BannerType }) => (
          <>
            <IconButton
              size="small"
              color="success"
              data-action="save"
              disabled={!params.data.id || !editedRows.has(params.data.id)}
              onClick={(e) => {
                e.stopPropagation();
                void handleSaveRow(params.data);
              }}
            >
              <SaveIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              data-action="delete"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedBanner(params.data);
                setDialogOpen(true);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </>
        ),
      },
    ],
    [editedRows, handleSaveRow],
  );

  const onCellClicked = useCallback(
    (event: CellClickedEvent<BannerType>) => {
      if (event.colDef.field === "id" && event.data?.id) {
        navigate(`/banner-maintenance/${event.data.id}`);
      }
    },
    [navigate],
  );

  const onCellValueChanged = useCallback((event: CellValueChangedEvent<BannerType>) => {
    const id = event.data?.id;
    if (id) {
      setEditedRows((prev) => new Set(prev).add(id));
    }
  }, []);

  const handleConfirmDelete = async () => {
    if (!selectedBanner?.id) return;
    try {
      await deleteBanner(selectedBanner.id).unwrap();
      setSuccessMsg(BANNER_LIST_TEXTS.deleteSuccess);
    } catch {
      setErrorMsg(BANNER_LIST_TEXTS.deleteError);
    } finally {
      setDialogOpen(false);
      setSelectedBanner(null);
    }
  };

  return (
    <PageLayout fullWidth showSearch={false}>
      <PageContentBox>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" fontWeight="bold" color="text.primary">
            {BANNER_LIST_TEXTS.pageTitle}
          </Typography>
          <Box display="flex" gap={1} alignItems="center">
            <TextField
              size="small"
              placeholder="Buscar..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5 }} /> }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/banner-maintenance/new")}
            >
              {BANNER_LIST_TEXTS.createButton}
            </Button>
          </Box>
        </Box>

        <div className="ag-theme-quartz" style={{ width: "100%", height: TABLE_HEIGHT }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            quickFilterText={searchText}
            overlayLoadingTemplate={"<span>Cargando banners...</span>"}
            overlayNoRowsTemplate={"<span>No hay banners disponibles.</span>"}
            onCellClicked={onCellClicked}
            onCellValueChanged={onCellValueChanged}
            singleClickEdit
            stopEditingWhenCellsLoseFocus
          />
        </div>
      </PageContentBox>

      <DeleteBannerDialog
        open={dialogOpen}
        bannerTitle={selectedBanner?.title ?? ""}
        isDeleting={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDialogOpen(false);
          setSelectedBanner(null);
        }}
      />

      <Snackbar
        open={!!successMsg}
        autoHideDuration={SNACKBAR_AUTO_HIDE_MS}
        onClose={() => setSuccessMsg("")}
      >
        <Alert severity="success" onClose={() => setSuccessMsg("")}>
          {successMsg}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!errorMsg}
        autoHideDuration={SNACKBAR_AUTO_HIDE_MS}
        onClose={() => setErrorMsg("")}
      >
        <Alert severity="error" onClose={() => setErrorMsg("")}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default BannerListPage;
